import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, MessageCircle, Send, Download } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { contact, services, siteSettings } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteContent } from "@/i18n/SiteContentContext";
import { sendInquiryEmail } from "@/lib/emailjs";

const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 px-5 py-3.5 text-sm text-white placeholder-white/40 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/20";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const { get } = useSiteContent();
  const info = get("contact", contact);
  const settings = get("settings", siteSettings);
  const serviceList = get("services", services);
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const infoItems = [
    { icon: Phone, label: t("contact.phone"), value: info.phone },
    { icon: Mail, label: t("contact.email"), value: info.email },
    { icon: Clock, label: t("contact.hours"), value: info.hours },
    { icon: MapPin, label: t("contact.office"), value: info.office },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendInquiryEmail(form);
      await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "contact-form" }),
      });
      toast.success("Inquiry received — our team will reach out within 24 hours.");
      setForm({ name: "", company: "", phone: "", email: "", service: "", message: "" });
    } catch {
      toast.error("Could not send — please try again or use WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="honeycomb-bg-light relative overflow-hidden bg-navy py-28 md:py-40">
      <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-sky/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-20 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          dark
          number="08"
          eyebrow={t("contact.eyebrow")}
          title={t("contact.title")}
          description={t("contact.desc")}
        />

        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col gap-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {infoItems.map((item) => (
                  <div key={item.label} data-testid={`contact-info-${item.label.toLowerCase().replace(" ", "-")}`} className="glass-dark hex-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40">
                    <item.icon className="h-6 w-6 text-gold" />
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40">{item.label}</p>
                    <p className="mt-1 font-jakarta text-sm font-bold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/50">
                Contact Person: <span className="font-semibold text-white/80">{info.person}</span> · {info.hoursNote}
              </p>
              <a href={settings.profile_pdf} download data-testid="contact-profile-btn" className="inline-flex w-fit items-center gap-2 rounded-full border border-gold/40 px-6 py-3 font-jakarta text-sm font-semibold text-gold transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-navy">
                <Download className="h-4 w-4" /> Download Company Profile (PDF)
              </a>
              <div className="hex-card min-h-64 flex-1 overflow-hidden border border-white/10 shadow-2xl">
                <iframe
                  title="Mandair Movers — Abu Dhabi"
                  data-testid="contact-map"
                  src="https://maps.google.com/maps?q=Mafraq%20Industrial%20Area%20Abu%20Dhabi&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  className="h-full min-h-64 w-full border-0"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="glass-dark hex-card space-y-5 p-8 md:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <input required data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder={t("form.name")} className={inputCls} />
                <input data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder={t("form.company")} className={inputCls} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <input required data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("form.phone")} className={inputCls} />
                <input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder={t("form.email")} className={inputCls} />
              </div>
              <select required data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} text-white/70`}>
                <option value="" disabled className="text-navy">{t("form.service")}</option>
                {serviceList.map((s) => (
                  <option key={s.title} value={s.title} className="text-navy">{s.title}</option>
                ))}
              </select>
              <textarea required rows={4} data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder={t("form.message")} className={`${inputCls} resize-none`} />
              <div className="flex flex-wrap gap-4 pt-2">
                <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="btn-premium flex-1 disabled:opacity-60">
                  {sending ? "Sending…" : t("form.submit")} <Send className="h-4 w-4" />
                </button>
                <a href={`https://wa.me/${info.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-leaf/50 px-8 py-4 font-jakarta text-sm font-semibold tracking-wide text-leaf transition-all duration-300 hover:-translate-y-1 hover:bg-leaf hover:text-white hover:shadow-xl hover:shadow-leaf/30">
                  <MessageCircle className="h-4 w-4" /> {t("form.whatsapp")}
                </a>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
